import React, { useEffect, useState, Fragment } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import "./Sidebar.css";
import API_BASE_URL from "../config";
import logoDark from "../assets/pairfon-logo-horizontal-dark.png";
import logoLight from "../assets/pairfon-logo-horizontal-light.png";
import usePrefersDarkMode from "../hooks/usePrefersDarkMode";

export default function Sidebar() {
  const prefersDark = usePrefersDarkMode();
  const logo = logoDark; // white logo — sidebar is dark
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [services, setServices] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) return;
    axios
      .get(`${API_BASE_URL}/services`, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 })
      .then((res) => setServices(res.data.services || {}))
      .catch(() => {});
  }, []);

  const hasService = (s) => services[s]?.status === "active";

  useEffect(() => {
    const toggle = () => {
      if (window.innerWidth < 992) {
        setDrawerOpen((prev) => !prev);
      }
    };
    const close = () => setDrawerOpen(false);
    window.addEventListener("pairfon-sidebar-toggle", toggle);
    window.addEventListener("pairfon-sidebar-close", close);
    return () => {
      window.removeEventListener("pairfon-sidebar-toggle", toggle);
      window.removeEventListener("pairfon-sidebar-close", close);
    };
  }, []);

  useEffect(() => {
    const syncWidth = () => {
      if (window.innerWidth >= 992) {
        setDrawerOpen(false);
      }
    };
    window.addEventListener("resize", syncWidth);
    return () => window.removeEventListener("resize", syncWidth);
  }, []);

  const handleNavigate = () => {
    if (typeof window === "undefined") {
      return;
    }
    if (window.innerWidth < 992) {
      setDrawerOpen(false);
    }
  };

  return (
    <Fragment>
      {drawerOpen && <div className="sidebar-backdrop show" onClick={handleNavigate} />}
      <aside className={`sidebar${drawerOpen ? " open" : ""}`}>
        <div className="sidebar-header">
          <img src={logo} alt="Pairfon logo" className="sidebar-logo" />
          <button
            type="button"
            className="sidebar-close d-lg-none"
            aria-label="Close navigation"
            onClick={handleNavigate}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <ul className="nav flex-column">
          {/* ── Services (only the ones the company pays for) ── */}
          <li className="nav-item">
            <NavLink to="/dashboard" className="nav-link" onClick={handleNavigate}>
              <i className="bi bi-speedometer2"></i> <span className="nav-text">Dashboard</span>
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink to="/services" className="nav-link" onClick={handleNavigate}>
              <i className="bi bi-grid"></i> <span className="nav-text">Services</span>
            </NavLink>
          </li>
          {hasService("sms") && (
            <li className="nav-item">
              <NavLink to="/devices" className="nav-link" onClick={handleNavigate}>
                <i className="bi bi-android2"></i> <span className="nav-text">Android</span>
              </NavLink>
            </li>
          )}
          {hasService("whatsapp") && (
            <li className="nav-item">
              <NavLink to="/whatsapp" className="nav-link" onClick={handleNavigate}>
                <i className="bi bi-whatsapp"></i> <span className="nav-text">WhatsApp</span>
              </NavLink>
            </li>
          )}
          {hasService("imessage") && (
            <li className="nav-item">
              <NavLink to="/imessage" className="nav-link" onClick={handleNavigate}>
                <i className="bi bi-chat-fill"></i> <span className="nav-text">iMessage</span>
              </NavLink>
            </li>
          )}
          {/* Chat hidden for now (route/page kept in code; API-first focus).
          {anyService && (
            <li className="nav-item">
              <NavLink to="/chat" className="nav-link" onClick={handleNavigate}>
                <i className="bi bi-chat-dots"></i> <span className="nav-text">Chat</span>
              </NavLink>
            </li>
          )} */}

          <li className="sidebar-divider" aria-hidden="true"></li>

          {/* ── Account & integration ── */}
          <li className="nav-item">
            <NavLink to="/mobile-app" className="nav-link" onClick={handleNavigate}>
              <i className="bi bi-cloud-arrow-down"></i> <span className="nav-text">Mobile App</span>
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink to="/api" className="nav-link" onClick={handleNavigate}>
              <i className="bi bi-key"></i> <span className="nav-text">API & Webhooks</span>
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink to="/integration" className="nav-link" onClick={handleNavigate}>
              <i className="bi bi-plug"></i> <span className="nav-text">Integration</span>
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink to="/company" className="nav-link" onClick={handleNavigate}>
              <i className="bi bi-building"></i> <span className="nav-text">Company</span>
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink to="/users" className="nav-link" onClick={handleNavigate}>
              <i className="bi bi-people"></i> <span className="nav-text">Users</span>
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink to="/profile" className="nav-link" onClick={handleNavigate}>
              <i className="bi bi-person"></i> <span className="nav-text">Profile</span>
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink to="/billing" className="nav-link" onClick={handleNavigate}>
              <i className="bi bi-credit-card"></i> <span className="nav-text">Billing</span>
            </NavLink>
          </li>
        </ul>
      </aside>
    </Fragment>
  );
}
